// The IDs and states the Curator API contract's schemas carry: branded IDs,
// the listing state, a toolset's blockers and its hub secret's state. Part
// of the Curator API contract, so it imports only effect. The platform
// re-exports them from src/domain/types.ts, and ToolsetStatus from
// src/hubs/toolsets.ts. See Design > Names and
// definitions, "Curator API contract".

import { Schema } from "effect";

// IDs that cross a service are branded, so tsc refuses one where another
// belongs. Each is built only by the decoders and constructors named beside
// it. See Design > Code standards, "IDs that cross a service are branded".

// The linked repository's name. Built by the decoders of D1 hub and toolset
// rows (src/hubs/toolsets.ts) and of an answer to refund (AnswerToRefund,
// src/services/store.ts); of the intent index's result rows (ResultRow,
// src/services/intent-index.ts); at registration, from WarmHub's answer
// (src/exchange/registry.ts); by indexRows (src/hubs/index-rows.ts), from a manifest staging checked
// against its hub record; and in the ToolHost from its sealed run
// (src/hubs/tool-run-seal.ts). See Design > Names and definitions, "Hub name".
export const HubName = Schema.String.pipe(Schema.brand("HubName"));
export type HubName = typeof HubName.Type;

// The Hub ID: how the platform identifies a hub. Its value is the linked
// repository's durable ID, the key of the hub's record. It never comes from
// a request. It's built by the decoders of D1 rows: hub rows
// (src/hubs/toolsets.ts) and an answer to refund (AnswerToRefund,
// src/services/store.ts); from WarmHub's answer for the repository, at
// registration (src/exchange/registry.ts); and by the sealed run
// (src/hubs/tool-run-seal.ts), which is trusted only once its ToolRun seal
// verifies, and the Exchange sealed a stored Hub ID. See Design > Names and
// definitions, "Hub ID". A Hub ID is never empty.
export const HubId = Schema.String.check(Schema.isNonEmpty()).pipe(Schema.brand("HubId"));
export type HubId = typeof HubId.Type;

// A toolset's commit SHA in its toolset repository: 40 hex digits (SHA-1)
// or 64 (SHA-256). Decoded from routes, push events and workflow params.
// See Design > Names and definitions, "Toolset".
export const CommitSha = Schema.String.check(Schema.isPattern(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/)).pipe(Schema.brand("CommitSha"));
export type CommitSha = typeof CommitSha.Type;
// A commit SHA as a D1 row stores it, decoded without the pattern check:
// staging writes only a CommitSha, but one row from before it that doesn't
// match must not fail a read of every toolset. src decodes it from D1 rows
// only; its .make is for tests.
export const StoredCommitSha = Schema.String.pipe(Schema.brand("CommitSha"));

// A test run's ID: assigned when the run starts, and decoded from routes,
// workflow params and D1 rows. See Design > Names and definitions, "Test run".
export const TestRunId = Schema.String.check(Schema.isUUID(4)).pipe(Schema.brand("TestRunId"));
export type TestRunId = typeof TestRunId.Type;
// A hub job's ID: assigned when the operator starts it, and decoded from
// routes, queue messages and D1 rows. See Design > Names and definitions,
// "Hub job".
export const HubJobId = Schema.String.check(Schema.isUUID(4)).pipe(Schema.brand("HubJobId"));
export type HubJobId = typeof HubJobId.Type;
// What a hub job does to each hub. See Design > Operations, "Hub jobs".
export const HubJobKind = Schema.Literals(["reseal"]);
export type HubJobKind = typeof HubJobKind.Type;

// A hub's listing state. See Design > Names and definitions, "Listing state".
export const LISTINGS = ["never_listed", "awaiting_approval", "listed", "unlisted", "archived"] as const;
export const Listing = Schema.Literals(LISTINGS);
export type Listing = typeof Listing.Type;

// One unmet activation check for a staged toolset. Studio and agents choose
// the next action from `_tag`; `detail` is display text that code never
// matches on. See Design > Names and definitions, "Blocker".
export const Blocker = Schema.Union([
  // `unreadable` is the part of `missing` that a grant can't fix: the token the
  // hub reads with can't read it. Empty when none is, or the store doesn't say.
  Schema.TaggedStruct("HubSecretMissing", { detail: Schema.String, missing: Schema.Array(Schema.String), unreadable: Schema.Array(Schema.String) }),
  Schema.TaggedStruct("ExamplesNotRun", { detail: Schema.String }),
  Schema.TaggedStruct("ExamplesRunning", { detail: Schema.String, runId: TestRunId }),
  Schema.TaggedStruct("ExamplesFailed", { detail: Schema.String, runId: TestRunId }),
  Schema.TaggedStruct("GoldenNotRun", { detail: Schema.String }),
  Schema.TaggedStruct("GoldenRunning", { detail: Schema.String, runId: TestRunId }),
  Schema.TaggedStruct("NotStaged", { detail: Schema.String, status: Schema.Literals(["validating", "rejected"]) }),
  Schema.TaggedStruct("HubArchived", { detail: Schema.String }),
  Schema.TaggedStruct("ToolSdkUnsupported", { detail: Schema.String, sdkVersion: Schema.String, supported: Schema.Array(Schema.String) }),
]);
export type Blocker = typeof Blocker.Type;

// A hub secret's state as the Curator API returns it, never its value.
// `tokenName` names the token for the notice that asks a curator to revoke it
// in WarmHub. See Design > Names and definitions, "HubSecretState".
export const HubSecretState = Schema.Struct({
  state: Schema.Literals(["Live", "Expiring", "Expired", "Refused", "Missing"]),
  expiresAt: Schema.NullOr(Schema.Finite),
  checkedAt: Schema.NullOr(Schema.Finite),
  tokenName: Schema.NullOr(Schema.String),
});
export type HubSecretState = typeof HubSecretState.Type;

// A hub's API key for one header or query parameter of one host as the
// Curator API returns it, never a secret or a token: the host's account it
// is for, "default" unless the curator named one; where it's sent (`in`
// a header or the query, under `name`), with a header's prefix, and
// whether the host refused it (a 401); whether it is static or a grant, and
// which, when its stored API key token expires, how many times it was
// renewed and when last; and the credential's own expiry, as the curator
// gave it. See Design > Names and definitions, "ApiKeyState".
export const ApiKeyState = Schema.Struct({
  host: Schema.String,
  account: Schema.String,
  in: Schema.Literals(["header", "query"]),
  name: Schema.String,
  prefix: Schema.NullOr(Schema.String),
  state: Schema.Literals(["Live", "Refused"]),
  refusedAt: Schema.NullOr(Schema.Finite),
  kind: Schema.Literals(["static", "grant"]),
  grantType: Schema.NullOr(Schema.Literals(["key_exchange", "client_credentials", "refresh_token", "jwt_bearer"])),
  tokenExpiresAt: Schema.NullOr(Schema.Finite),
  generation: Schema.Int,
  lastRenewedAt: Schema.NullOr(Schema.Finite),
  expiresAt: Schema.NullOr(Schema.Finite),
  createdBy: Schema.String,
  createdAt: Schema.Finite,
});
export type ApiKeyState = typeof ApiKeyState.Type;

// Every secret a hub holds, never a value: its WarmHub tokens, each with the
// repositories it reads and the toolsets that read with it, its API keys,
// and its Hub token's state, or null when no curator has connected one;
// `warmhub` never lists the Hub token. See Design > Names
// and definitions, "HubSecretsSummary".
export const HubSecretsSummary = Schema.Struct({
  warmhub: Schema.Array(Schema.Struct({ secret: HubSecretState, repos: Schema.Array(Schema.String), toolsets: Schema.Array(Schema.String) })),
  apiKeys: Schema.Array(ApiKeyState),
  hubToken: Schema.NullOr(HubSecretState),
});
export type HubSecretsSummary = typeof HubSecretsSummary.Type;

// "validating" and "rejected" are written only by staging; a hub's listing
// never includes them. See Design > HubRouter, "Toolsets".
export const ToolsetStatus = Schema.Literals(["validating", "staged", "rejected", "active"]);
export type ToolsetStatus = typeof ToolsetStatus.Type;
